import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { requireFeature } from "@/lib/feature-gate";
export const Route = createFileRoute("/user/_layout/expense")({
  beforeLoad: () => requireFeature("expenses"),
  component: UserExpensePage,
});

import { useState, useEffect } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import FiltersPanel from "@/components/reusable-components/header/filter-pannel";
import SearchFilter from "@/components/reusable-components/header/search-filter";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { SelectFilter } from "@/components/crud/filters/SelectFilter";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ExpenseReportTable } from "@/components/expense/tables/ExpenseReportTable";
import { ExpenseReportCreateSheet } from "@/components/expense/forms/ExpenseReportCreateSheet";
import { useDebounce } from "@/hooks/use-debounce";
import { useDataTableOrdering } from "@/hooks/ui/useDataTableOrdering";
import {
  useFetchExpenseReports,
  useExpenseReportMutations,
  type ExpenseReportOrdering,
  type ExpenseReportOrderingField,
} from "@/hooks/api/expense/useExpenseReports";
import type { ExpenseReport, ExpenseStatus } from "@/types/expense";
import type { SortDirection } from "@/components/reusable-components/sort-arrow";
import { useOrgSettingsStore } from "@/stores/useOrgSettingsStore";
import { useAuthStore } from "@/stores/useAuthStore";

const STATUS_OPTIONS = [
  { label: "Draft", value: "DRAFT" },
  { label: "Submitted", value: "SUBMITTED" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Paid", value: "PAID" },
] satisfies { label: string; value: ExpenseStatus }[];

function UserExpensePage() {
  const expenseApprovalRequired = useOrgSettingsStore(
    (s) => s.settings?.expense_approval_required ?? true
  );
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);

  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ExpenseStatus | undefined>(
    undefined
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch] = useDebounce(searchQuery, 500);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ExpenseReport | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ExpenseReport | null>(null);

  const { ordering, toggleOrdering, getApiOrdering } = useDataTableOrdering<
    {
      title: SortDirection;
      period_start: SortDirection;
      total_amount: SortDirection;
      created_at: SortDirection;
    },
    ExpenseReportOrdering
  >({ title: null, period_start: null, total_amount: null, created_at: null });

  const reportsQuery = useFetchExpenseReports({
    page_size: pageSize,
    page,
    ordering: getApiOrdering(),
    search: debouncedSearch || undefined,
    // axios drops undefined params, so no selection means no status filter.
    status: statusFilter,
  });
  const reports = reportsQuery.data?.results ?? [];
  const count = reportsQuery.data?.count ?? 0;

  const { submitReport, cancelReport } = useExpenseReportMutations();

  const isMutating = submitReport.isPending || cancelReport.isPending;
  const isLoading = reportsQuery.isPending || isMutating;
  const isFetching = reportsQuery.isFetching || isMutating;

  // Back to page 1 whenever a filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, ordering, statusFilter, setPage]);

  const handleOrderingChange = (field: ExpenseReportOrderingField) => {
    toggleOrdering(field);
  };

  const handleSubmitReport = async (report: ExpenseReport) => {
    try {
      await submitReport.mutateAsync({ id: report.id });
      toast.success(
        expenseApprovalRequired
          ? "Report submitted for approval"
          : "Report submitted"
      );
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    try {
      await cancelReport.mutateAsync({ id: cancelTarget.id });
      toast.success("Report cancelled");
      setCancelTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="My Expenses"
          description="Track and submit your expense reports for reimbursement."
          onToggleFilter={() => setShowFilters((p) => !p)}
          onAdd={() => setCreateSheetOpen(true)}
          addLabel="New Report"
          isLoading={isFetching}
        />

        <FiltersPanel
          show={showFilters}
          onClear={() => {
            setStatusFilter(undefined);
            setSearchQuery("");
          }}
          isLoading={isFetching}
        >
          <SearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search reports..."
          />
          <SelectFilter
            placeholder="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
        </FiltersPanel>

        <ExpenseReportTable
          reports={reports}
          isLoading={isLoading}
          ordering={ordering}
          onOrderingChange={handleOrderingChange}
          onView={(r) =>
            navigate({ to: "/user/expense/$id", params: { id: String(r.id) } })
          }
          onEdit={(r) => setEditTarget(r)}
          onSubmit={handleSubmitReport}
          onCancel={(r) => setCancelTarget(r)}
          currentUserId={authUser?.user_id}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={setPage}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
      </div>

      <ExpenseReportCreateSheet
        isOpen={createSheetOpen}
        onClose={() => setCreateSheetOpen(false)}
      />
      <ExpenseReportCreateSheet
        isOpen={!!editTarget}
        editingReport={editTarget ?? undefined}
        onClose={() => setEditTarget(null)}
      />

      <DeleteConfirmDialog
        isOpen={!!cancelTarget}
        onClose={() => setCancelTarget(null)}
        onConfirm={handleCancelConfirm}
        isLoading={cancelReport.isPending}
        title="Cancel Report"
        description={`Cancel "${cancelTarget?.title}"? This cannot be undone.`}
      />
    </SidebarInset>
  );
}
